/**
 * Bug report models — wire shapes returned by the customer-facing
 * /api/bug-reports endpoint and the admin /api/support/bug-reports
 * endpoints. Mirrors the App\Entity\BugReport / BugReportPhoto serializer
 * groups (`bug_report:read`, `bug_report:list`, `bug_report:admin`).
 */

/**
 * Lifecycle status — mirrors App\Enum\BugReportStatus.
 * Kept intentionally small; expand here if the backend grows the workflow.
 */
export enum BugReportStatus {
  OPEN = 1,
  RESOLVED = 2,
}

/**
 * Optional diagnostic snapshot captured by the front-end at submit time.
 * Opaque on the wire — anything serializable can go in; admins read it for
 * triage. Stored as JSON; backend caps the encoded payload at 64 KB.
 *
 * The shape below is suggestive, not enforced: extra fields pass through
 * unchanged. Keep entries small and PII-free.
 */
export interface BugReportClientContext {
  userAgent?: string;
  viewport?: { width: number; height: number };
  route?: string;
  jsError?: {
    message: string;
    stack?: string;
    source?: string;
    lineno?: number;
    colno?: number;
  };
  failingRequest?: {
    url: string;
    method?: string;
    status?: number;
    body?: string;
  };
  breadcrumbs?: Array<{ ts: string; type: string; data?: unknown }>;
  [extra: string]: unknown;
}

/**
 * Screenshot attached to a bug report. Mirrors the storage trio of
 * Photo / GalleryPhoto (serverId, serverName, pathStructureId); the
 * public `url` field is injected server-side by EntityNormalizer's
 * FileUrlResolvableInterface branch — clients shouldn't reconstruct it.
 *
 * Only fields exposed under `bug_report:read` are typed here. Width/height/
 * size are decimal strings on the wire (Doctrine DECIMAL columns).
 */
export interface BugReportPhotoInfo {
  id: number;
  serverName: string;
  originalName: string;
  serverId: number | null;
  pathStructureId: number | null;
  size: string | null;
  width: string | null;
  height: string | null;
  importDate: string | null;
  /** Public URL injected server-side via FileUrlResolvableInterface. */
  url?: string;
}

/**
 * Bug report record. Field availability depends on the serialization
 * group used by the endpoint that returned it:
 *
 *   - `bug_report:list`   — list endpoint base shape (no photos, no updateDate).
 *   - `bug_report:read`   — full shape including photos[] and photoCount.
 *   - `bug_report:admin`  — adds clientContext, customerEmail, customerName.
 *
 * All non-list fields are typed optional so a single interface can carry
 * either shape; the caller knows which endpoint it called.
 */
export interface BugReportInfo {
  id: number;
  customerId: number | null;
  description: string;
  status: BugReportStatus;
  statusLabel: string;
  creationDate: string;
  resolvedDate: string | null;

  // bug_report:read only
  updateDate?: string | null;
  photos?: BugReportPhotoInfo[];
  photoCount?: number;

  // bug_report:admin only
  clientContext?: BugReportClientContext | null;
  customerEmail?: string | null;
  customerName?: string | null;

  /**
   * Set when the report was filed during a support-impersonation session
   * (admin groups only — both list and detail endpoints). The customer*
   * fields then identify the support AGENT who wrote the report, and these
   * identify the customer whose account the agent was driving.
   *
   * Edge case: when the backend can't resolve the agent from the token's
   * `imp` claim it keeps the report on the principal and sets the
   * impersonated fields to that SAME principal — equal ids still mean
   * "filed under impersonation", not a data error.
   */
  impersonatedCustomerId?: number | null;
  impersonatedCustomerEmail?: string | null;
  impersonatedCustomerName?: string | null;

  /**
   * Thread summary, bulk-hydrated by the backend on every list/detail
   * response (`bug_report:list` + `bug_report:read`). `unreadMessageCount`
   * is relative to the CALLER's side: on the customer endpoints it counts
   * support messages the customer hasn't read; on the admin endpoints it
   * counts customer messages support hasn't read. `lastMessageAt` is
   * `Y-m-d H:i:s` or null when the thread is empty.
   */
  unreadMessageCount?: number;
  lastMessageAt?: string | null;
}

/** Which side of a bug-report thread wrote a message. */
export type BugReportMessageSender = 'customer' | 'support';

/**
 * One message of a report's customer ↔ support thread
 * (`bug_report_message:read`). Customer-facing payloads expose senderType,
 * senderName and `readByCustomerAt` ONLY — `senderId` and `readBySupportAt`
 * (support's own read receipt) are admin-group fields and never reach the
 * customer. A customer-side message posted under support impersonation is a
 * SUPPORT message from the agent (backend rule) — never trust the endpoint
 * you called to infer the side, read `senderType`.
 */
export interface BugReportMessageInfo {
  id: number;
  bugReportId: number;
  senderType: BugReportMessageSender;
  /** Sender's display name; support messages may say "Support" when the agent has none. */
  senderName: string;
  body: string;
  /** `Y-m-d H:i:s` */
  createdAt: string;
  /**
   * When the CUSTOMER marked this (support) message read. Stays null on the
   * customer's own messages — it does not mean "unread by support".
   */
  readByCustomerAt: string | null;
  /** Admin group only — when support marked this (customer) message read. */
  readBySupportAt?: string | null;
  /** Admin group only. */
  senderId?: number | null;

  /**
   * Photos attached to THIS message (same storage shape as the report's own
   * photos, `url` injected server-side). Absent on messages stored before
   * attachments shipped — treat as empty.
   */
  photos?: BugReportPhotoInfo[];
  photoCount?: number;
}

/**
 * 201 envelope of the send-message endpoints (same shape as report create).
 * `photosFailed` carries per-file "name: reason" strings for attachments that
 * couldn't be stored — the message itself WAS saved with the rest, so surface
 * them as a non-blocking warning. A photo-only message whose every photo
 * failed is a 422 `photos_failed` instead (nothing saved).
 */
export interface BugReportMessageCreateResponse {
  message: BugReportMessageInfo;
  photosFailed: string[];
}

/**
 * Attachment limits, enforced identically by the backend on a report's own
 * photos and on a thread message's. Single source of truth for BOTH pickers
 * (the create dialog and the thread composer) — don't re-declare them.
 */
export const BUG_REPORT_PHOTO_MAX_COUNT = 5;
export const BUG_REPORT_PHOTO_MAX_BYTES = 10 * 1024 * 1024;
export const BUG_REPORT_PHOTO_MAX_MB = BUG_REPORT_PHOTO_MAX_BYTES / (1024 * 1024);
export const BUG_REPORT_PHOTO_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Soft ceiling on the COMBINED size of one upload. Five 10 MB photos is a
 * 50 MB body, which can exceed PHP's `post_max_size` before any application
 * code runs — the server then answers `413 payload_too_large` (BaseController
 * ::readMultipartPhotos detects the empty $_POST/$_FILES case).
 *
 * The real limit is server configuration we can't read from here, so this is
 * a WARNING threshold, not a block: crossing it shows "this may be too large"
 * and a 413 is still handled cleanly. Set it to the deployment's actual
 * post_max_size and it becomes an accurate pre-flight check.
 */
export const BUG_REPORT_PHOTO_TOTAL_WARN_BYTES = 20 * 1024 * 1024;

/**
 * Error body of the bug-report endpoints — always `{error, code}`, branch on
 * `code`:
 *   422 `body_required`        no text AND no photo
 *   422 `body_too_long`        > 5000 chars
 *   422 `too_many_photos`      more than 5 photos on one message (nothing uploaded)
 *   422 `photos_failed`        photo-only message and EVERY photo failed (nothing
 *                              saved); the per-file reasons are in `error`
 *   400 `invalid_body`         request wasn't a JSON object / wrong types (client bug)
 *   400 `invalid_status`       `?status=` not 1|2 (client bug)
 *   403 `impersonator_invalid` write from a support-impersonation session whose
 *                              agent is no longer valid staff — nothing saved
 *   404 (no code)              report not owned / deleted — "not found", never permission
 */
export interface BugReportApiError {
  error: string;
  code?: 'body_required' | 'body_too_long' | 'too_many_photos' | 'photos_failed'
    | 'invalid_body' | 'invalid_status' | 'impersonator_invalid' | string;
}

export const BUG_REPORT_MESSAGE_MAX_LENGTH = 5000;

/**
 * Successful create response (201) from POST /api/bug-reports.
 * `photosFailed` carries human-readable per-file failure strings — the
 * report itself was created regardless, so this is informational.
 */
export interface BugReportCreateResponse {
  report: BugReportInfo;
  photosFailed: string[];
}

/**
 * 422 validation error body from POST /api/bug-reports. Keys map 1:1 to
 * the form fields that failed (`description`, `photos`, `clientContext`).
 * Surface them inline next to each input.
 */
export interface BugReportCreateValidationError {
  success: false;
  errors: Partial<Record<'description' | 'photos' | 'clientContext', string>>;
}

/**
 * Filters accepted by GET /api/support/bug-reports. `sort` accepts a
 * field name with an optional `-` prefix for DESC; valid fields are
 * `creationDate` (default), `status`, `id`, `resolvedDate`. Unknown
 * values fall back to `-creationDate` server-side.
 *
 * `from` / `to` are date strings parsed by PHP's `new DateTime(...)` —
 * `YYYY-MM-DD` is the safe form; ISO 8601 also works.
 */
export interface BugReportSearchFilters {
  status?: BugReportStatus;
  customerId?: number;
  /** Partial, case-insensitive match against customer email (LIKE %x%). */
  email?: string;
  from?: string;
  to?: string;
  sort?: BugReportSortKey;
  /** Only reports with customer messages support hasn't read yet. */
  hasUnread?: boolean;
}

export type BugReportSortField = 'creationDate' | 'status' | 'id' | 'resolvedDate';
export type BugReportSortKey = BugReportSortField | `-${BugReportSortField}`;

/**
 * Standard paginated envelope returned by GET /api/support/bug-reports.
 * `from`/`to` are 1-based inclusive offsets for "Showing X–Y of N" footers.
 */
export interface BugReportListPage {
  items: BugReportInfo[];
  pagination: {
    current_page: number;
    limit: number | null;
    total: number;
    total_pages: number;
    from: number;
    to: number;
  };
}
